/**
 * Telemetry — Phase 2.6 scaffold.
 *
 * Vendor-agnostic event sink. Pick your adapter at runtime — PostHog,
 * Plausible, Firestore-direct, etc. — without touching call sites.
 *
 * The point of this scaffold: instrument the codebase NOW with the right
 * event names + properties so the moment a vendor decision lands, you flip
 * one adapter import and the data starts flowing. No multi-week sweep.
 *
 * Event taxonomy (initial set — extend as features ship):
 *
 *   attendance.submit       { lessonId, studentCount, present, absent, late,
 *                             durationMs, offline }
 *   attendance.undo         { lessonId }
 *   lesson.create           { method: 'fab' | 'duplicate' | 'modal' }
 *   whatsapp.send           { scope: 'individual' | 'bulk', count }
 *   search.no_result        { query, scope }
 *   palette.open            {}
 *   palette.select          { kind: 'page' | 'student' | 'group' | 'action', id }
 *   offline.queued          { method, url }
 *   offline.flushed         { ok, failed }
 *   theme.change            { from, to }
 *
 * Naming convention: `domain.action` (snake_case for compound actions).
 */

const adapters = [];
const DEFAULT_PROPS = {};

/**
 * Register an adapter. Adapters are simple objects with these methods:
 *
 *   identify(userId, traits)     — associate session with a user
 *   track(eventName, props)      — fire an event
 *   reset()                      — sign-out cleanup
 *
 * Multiple adapters can be registered (e.g. PostHog + Sentry breadcrumbs).
 * Each is called with the same payload; one failing doesn't break others.
 */
export function registerAdapter(adapter) {
  if (!adapter || typeof adapter.track !== 'function') return;
  adapters.push(adapter);
}

/**
 * Drop all adapters (used by tests).
 */
export function _clearAdapters() {
  adapters.length = 0;
}

/**
 * Default properties merged into every event. Use for global context like
 * appVersion or centerType. Call early, before any track() invocations.
 */
export function setDefaultProps(props) {
  Object.assign(DEFAULT_PROPS, props || {});
}

export function clearDefaultProps() {
  Object.keys(DEFAULT_PROPS).forEach((k) => delete DEFAULT_PROPS[k]);
}

/**
 * Fire an event to every registered adapter.
 *
 * Errors thrown by a single adapter are swallowed so one broken integration
 * doesn't disable the rest.
 *
 * @param {string} eventName - lower.snake_case domain.action
 * @param {Object} [props]
 */
export function trackEvent(eventName, props = {}) {
  if (!eventName || typeof eventName !== 'string') return;
  const payload = { ...DEFAULT_PROPS, ...props };
  for (const a of adapters) {
    try {
      a.track(eventName, payload);
    } catch (_) {
      // swallow — one bad adapter must not kill the rest
    }
  }
}

export function identifyUser(userId, traits = {}) {
  if (!userId) return;
  for (const a of adapters) {
    try { a.identify?.(userId, traits); } catch (_) { /* swallow */ }
  }
}

export function resetTelemetry() {
  for (const a of adapters) {
    try { a.reset?.(); } catch (_) { /* swallow */ }
  }
}

// ─── Built-in adapters ─────────────────────────────────────────────────────

/**
 * Console adapter — useful in dev and for verifying event shapes without a
 * real vendor. Prefixes with `[telemetry]` for easy filtering.
 */
export function createConsoleAdapter() {
  return {
    track(name, props) {
      // eslint-disable-next-line no-console
      console.info('[telemetry]', name, props);
    },
    identify(userId, traits) {
      // eslint-disable-next-line no-console
      console.info('[telemetry] identify', userId, traits);
    },
    reset() {
      // eslint-disable-next-line no-console
      console.info('[telemetry] reset');
    },
  };
}

/**
 * Buffer adapter — collects events in memory. Used by tests and by the
 * "no-vendor-yet" production mode so we can still see how often events
 * fire when reviewing logs.
 */
export function createBufferAdapter({ max = 200 } = {}) {
  const buffer = [];
  return {
    buffer,
    track(name, props) {
      buffer.push({ name, props, at: Date.now() });
      if (buffer.length > max) buffer.shift();
    },
    identify() {},
    reset() { buffer.length = 0; },
  };
}

// ─── Auto-init ─────────────────────────────────────────────────────────────
//
// In development: register the console adapter so engineers see events fire
// as they work on features. In production: stay silent until a real adapter
// is wired up (typical pattern: import this from index.js, call
// `registerAdapter(createPostHogAdapter(...))` once auth is known).
if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
  registerAdapter(createConsoleAdapter());
}
